import { Capacitor } from '@capacitor/core'
import { AppUpdate, AppUpdateAvailability, AppUpdateInfo } from '@capawesome/capacitor-app-update'
import { useCallback, useState } from 'react'

interface AppUpdateState {
  isUpdateAvailable: boolean
  isCheckingUpdate: boolean
  currentVersion: string
  availableVersion: string
  updateInfo: AppUpdateInfo | null
}

interface AppUpdateActions {
  checkForUpdates: () => Promise<void>
  performImmediateUpdate: () => Promise<void>
  startFlexibleUpdate: () => Promise<void>
  completeFlexibleUpdate: () => Promise<void>
  openAppStore: () => Promise<void>
}

type UseAppUpdateReturn = AppUpdateState & AppUpdateActions

export const useAppUpdate = (): UseAppUpdateReturn => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
  const [isCheckingUpdate, setIsCheckingUpdate] = useState<boolean>(false)
  const [currentVersion, setCurrentVersion] = useState<string>('')
  const [availableVersion, setAvailableVersion] = useState<string>('')
  const [updateInfo, setUpdateInfo] = useState<AppUpdateInfo | null>(null)

  const getCurrentAppVersion = useCallback(async (): Promise<string> => {
    try {
      const result = await AppUpdate.getAppUpdateInfo()
      if (Capacitor.getPlatform() === 'android') {
        return result.currentVersionCode?.toString() ?? ''
      } else {
        return result.currentVersionName ?? ''
      }
    } catch (error) {
      console.error('Error getting current app version:', error)
      return ''
    }
  }, [])

  const getAvailableAppVersion = useCallback(async (): Promise<string> => {
    try {
      const result = await AppUpdate.getAppUpdateInfo()
      if (Capacitor.getPlatform() === 'android') {
        return result.availableVersionCode?.toString() ?? ''
      } else {
        return result.availableVersionName ?? ''
      }
    } catch (error) {
      console.error('Error getting available app version:', error)
      return ''
    }
  }, [])

  const checkForUpdates = useCallback(async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    setIsCheckingUpdate(true)

    try {
      const result = await AppUpdate.getAppUpdateInfo()

      setUpdateInfo(result)

      const current = await getCurrentAppVersion()
      const available = await getAvailableAppVersion()

      setCurrentVersion(current)
      setAvailableVersion(available)

      const updateAvailable = result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
      setIsUpdateAvailable(updateAvailable)
    } catch (error) {
      console.error('Error checking for updates:', error)
      setIsUpdateAvailable(false)
      setUpdateInfo(null)
    } finally {
      setIsCheckingUpdate(false)
    }
  }, [getCurrentAppVersion, getAvailableAppVersion])

  const openAppStore = useCallback(async (): Promise<void> => {
    try {
      await AppUpdate.openAppStore({
        appId: import.meta.env.VITE_IOS_APP_ID,
      })
    } catch (error) {
      console.error('Error opening app store:', error)
    }
  }, [])

  const performImmediateUpdate = useCallback(async (): Promise<void> => {
    try {
      const result = await AppUpdate.getAppUpdateInfo()

      if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        return
      }

      if (result.immediateUpdateAllowed) {
        await AppUpdate.performImmediateUpdate()
      } else {
        await openAppStore()
      }
    } catch (error) {
      console.error('Error performing immediate update:', error)
      await openAppStore()
    }
  }, [openAppStore])

  const startFlexibleUpdate = useCallback(async (): Promise<void> => {
    try {
      const result = await AppUpdate.getAppUpdateInfo()

      if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        return
      }

      if (result.flexibleUpdateAllowed) {
        await AppUpdate.startFlexibleUpdate()
      }
    } catch (error) {
      console.error('Error starting flexible update:', error)
    }
  }, [])

  const completeFlexibleUpdate = useCallback(async (): Promise<void> => {
    try {
      await AppUpdate.completeFlexibleUpdate()
    } catch (error) {
      console.error('Error completing flexible update:', error)
    }
  }, [])

  return {
    isUpdateAvailable,
    isCheckingUpdate,
    currentVersion,
    availableVersion,
    updateInfo,
    checkForUpdates,
    performImmediateUpdate,
    startFlexibleUpdate,
    completeFlexibleUpdate,
    openAppStore,
  }
}
